import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";
import QuickOrder from "@galarson/Icons/QuickOrder";
import { breakpointMediaQueries } from "@insite/mobius/utilities";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    quickOrderLink: getPageLinkByPageType(state, "QuickOrderPage")?.url,
});

const mapDispatchToProps = {};

export interface QuickOrderStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
}

export const quickOrderStyles: QuickOrderStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "primary.main",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
            color: ${getColor("primary.main")};
            border:3px solid ${getColor("primary.main")};
            white-space: nowrape;
            width: 100%;
            justify-content: center;
            background: ${getColor("secondary.contrast")};
            gap:5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            >span{
                margin: 0px;
                font-family: Franklin Gothic Demi !important;
                font-size: 15px; 
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 14px; 
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            }
            &:hover{
                background: ${getColor("secondary.main")};
                color: ${getColor("primary.contrast")};
                border-color:  ${getColor("secondary.main")};
            }
            &:hover > span{
                color: ${getColor("primary.contrast")} !important;
            }

            &:hover > span > svg path
            {
                stroke:  ${getColor("primary.contrast")} !important;
            }


        `,
    }
};

const iconMap = {
    cart: QuickOrder,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const QuickOrderLink: FC<Props> = ({
    quickOrderLink,
    fields: { visibilityState, icon: selectedIcon }
}) => {
    

    const styles = useMergeStyles("quickOrder", quickOrderStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    return (
        <Link
            {...routerLink}
            href={quickOrderLink ? quickOrderLink:"/QuickOrder"}
            icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
            data-test-selector="quickOrderLink"
        >
            {showLabel && `${translate("Quick Order")} `}
            
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(QuickOrderLink)),
    definition: {
        displayName: "Quick Order Link",
        icon: "cart-shopping-fast",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
